import jsPDF from 'jspdf';
import 'jspdf-autotable';
import api from '@/services/api';

// Generate QR-like verification pattern (simplified visual indicator)
const drawVerificationSeal = (doc, x, y, code) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Seal background
  doc.setFillColor(15, 23, 42); // slate-900
  doc.roundedRect(x, y, pageWidth - 28, 45, 3, 3, 'F');
  
  // Verification icon pattern
  doc.setFillColor(34, 197, 94); // green-500
  doc.circle(x + 20, y + 22.5, 12, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('✓', x + 16, y + 27);
  
  // Verification text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('DOCUMENTO VERIFICADO Y AUTENTICADO', x + 40, y + 12);
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Código de Verificación Legal:', x + 40, y + 22);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(34, 197, 94); // green-500
  doc.text(code, x + 40, y + 33);
  
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184); // slate-400
  doc.text('Verifique este documento en: notaryos.com/verificar', x + 40, y + 41);
};

// Request verification code from backend
export const requestVerificationCode = async (documentType, content, metadata = {}) => {
  try {
    const response = await api.post('/documents/verify/create', {
      document_type: documentType,
      content: content,
      metadata: metadata
    });
    return response.data;
  } catch (error) {
    console.error('Error requesting verification code:', error);
    // Return a fallback code for demo purposes
    const timestamp = new Date().toISOString().replace(/[-:T.Z]/g, '').slice(0, 14);
    const prefix = documentType === 'litigation' ? 'LIT' : 'IDV';
    return {
      verification_code: `${prefix}-${timestamp}-DEMO-XXXX`,
      is_demo: true
    };
  }
};

// PDF Generation for Litigation Analysis with Verification
export const generateLitigationPDF = async (analysis, verificationCode = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(30, 41, 59); // slate-800
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('NotaryOS', 14, 20);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Reporte de Análisis de Litigios', 14, 30);
  
  // Date
  doc.setFontSize(10);
  doc.text(`Fecha: ${new Date().toLocaleDateString('es-CL')}`, pageWidth - 50, 20);
  
  // Reset text color
  doc.setTextColor(0, 0, 0);
  
  let yPos = 50;
  
  // Main Score Box
  const riskColor = analysis.risk_level === 'CRÍTICO' ? [220, 38, 38] :
                    analysis.risk_level === 'ALTO' ? [234, 88, 12] :
                    analysis.risk_level === 'MODERADO' ? [202, 138, 4] : [22, 163, 74];
  
  doc.setFillColor(...riskColor);
  doc.roundedRect(14, yPos, pageWidth - 28, 35, 3, 3, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(`${analysis.litigation_probability}%`, 24, yPos + 22);
  
  doc.setFontSize(12);
  doc.text('Probabilidad de Litigio', 70, yPos + 15);
  doc.text(`Riesgo ${analysis.risk_level}`, 70, yPos + 27);
  
  // Potential Damages
  if (analysis.potential_damages) {
    doc.setFontSize(14);
    doc.text(`Daños Potenciales: $${analysis.potential_damages.average?.toLocaleString() || 'N/A'}`, pageWidth - 90, yPos + 22);
  }
  
  yPos += 45;
  doc.setTextColor(0, 0, 0);
  
  // Risk Factors Section
  if (analysis.risks_found?.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Factores de Riesgo Detectados', 14, yPos);
    yPos += 8;
    
    const riskData = analysis.risks_found.map(risk => [
      risk.severity.toUpperCase(),
      risk.factor,
      risk.suggestion,
      `+${risk.weight}%`
    ]);
    
    doc.autoTable({
      startY: yPos,
      head: [['Severidad', 'Factor', 'Sugerencia', 'Impacto']],
      body: riskData,
      theme: 'striped',
      headStyles: { fillColor: [51, 65, 85] },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 40 },
        2: { cellWidth: 80 },
        3: { cellWidth: 20 }
      },
      styles: { fontSize: 9 }
    });
    
    yPos = doc.lastAutoTable.finalY + 10;
  }
  
  // Protections Section
  if (analysis.protections_found?.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Protecciones Encontradas', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const protections = analysis.protections_found.map(p => p.factor).join(', ');
    doc.text(protections, 14, yPos, { maxWidth: pageWidth - 28 });
    yPos += 15;
  }
  
  // Similar Cases Section
  if (analysis.similar_cases?.length > 0) {
    // Check if we need a new page
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Jurisprudencia Similar', 14, yPos);
    yPos += 8;
    
    const casesData = analysis.similar_cases.slice(0, 5).map(caso => [
      caso.id,
      caso.tribunal,
      caso.materia,
      caso.resultado,
      caso.indemnizacion > 0 ? `$${caso.indemnizacion.toLocaleString()}` : '-'
    ]);
    
    doc.autoTable({
      startY: yPos,
      head: [['Rol', 'Tribunal', 'Materia', 'Resultado', 'Indemnización']],
      body: casesData,
      theme: 'striped',
      headStyles: { fillColor: [51, 65, 85] },
      styles: { fontSize: 8 }
    });
    
    yPos = doc.lastAutoTable.finalY + 10;
  }
  
  // Recommendations Section
  if (analysis.recommendations?.length > 0) {
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Recomendaciones', 14, yPos);
    yPos += 8;
    
    const recsData = analysis.recommendations.map(rec => [
      rec.priority,
      rec.action,
      rec.impact || ''
    ]);
    
    doc.autoTable({
      startY: yPos,
      head: [['Prioridad', 'Acción', 'Impacto']],
      body: recsData,
      theme: 'striped',
      headStyles: { fillColor: [51, 65, 85] },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 100 },
        2: { cellWidth: 40 }
      },
      styles: { fontSize: 9 }
    });
  }
  
  // Add Verification Seal on last page if verification code provided
  if (verificationCode) {
    const lastPage = doc.internal.getNumberOfPages();
    doc.setPage(lastPage);
    const pageHeight = doc.internal.pageSize.getHeight();
    drawVerificationSeal(doc, 14, pageHeight - 65, verificationCode);
  }
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    const footerY = verificationCode && i === pageCount ? doc.internal.pageSize.getHeight() - 70 : doc.internal.pageSize.getHeight() - 10;
    doc.text(
      `Generado por NotaryOS - Página ${i} de ${pageCount}`,
      pageWidth / 2,
      footerY,
      { align: 'center' }
    );
    if (i !== pageCount || !verificationCode) {
      doc.text(
        'Este reporte es informativo y no constituye asesoría legal.',
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 5,
        { align: 'center' }
      );
    }
  }
  
  return doc;
};

// PDF Generation for Identity Verification with Verification Code
export const generateIdentityPDF = async (result, verificationCode = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Header
  doc.setFillColor(30, 41, 59);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('NotaryOS', 14, 20);
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Reporte de Verificación de Identidad', 14, 30);
  
  doc.setFontSize(10);
  doc.text(`Fecha: ${new Date().toLocaleDateString('es-CL')}`, pageWidth - 50, 20);
  doc.text(`ID: ${result.verification_id}`, pageWidth - 50, 28);
  
  doc.setTextColor(0, 0, 0);
  
  let yPos = 50;
  
  // Risk Score Box
  const scoreColor = result.risk_score.color === 'green' ? [22, 163, 74] :
                     result.risk_score.color === 'yellow' ? [202, 138, 4] :
                     result.risk_score.color === 'orange' ? [234, 88, 12] : [220, 38, 38];
  
  doc.setFillColor(...scoreColor);
  doc.roundedRect(14, yPos, pageWidth - 28, 35, 3, 3, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(`${result.risk_score.score}/100`, 24, yPos + 22);
  
  doc.setFontSize(12);
  doc.text('Score de Riesgo para Contratar', 85, yPos + 15);
  doc.text(`Nivel: ${result.risk_score.level}`, 85, yPos + 27);
  
  yPos += 45;
  doc.setTextColor(0, 0, 0);
  
  // Identity Information
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Información de Identidad', 14, yPos);
  yPos += 8;
  
  if (result.identity?.verified) {
    const identityData = result.identity.type === 'persona' ? [
      ['RUT', result.rut],
      ['Nombre Completo', result.identity.nombre_completo || ''],
      ['Nacionalidad', result.identity.nacionalidad || ''],
      ['Estado Civil', result.identity.estado_civil || ''],
      ['Fecha Nacimiento', result.identity.fecha_nacimiento || '']
    ] : [
      ['RUT', result.rut],
      ['Razón Social', result.identity.razon_social || ''],
      ['Situación', result.identity.situacion || ''],
      ['Inicio Actividades', result.identity.inicio_actividades || '']
    ];
    
    doc.autoTable({
      startY: yPos,
      body: identityData,
      theme: 'plain',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 45 },
        1: { cellWidth: 120 }
      },
      styles: { fontSize: 10 }
    });
    
    yPos = doc.lastAutoTable.finalY + 10;
  }
  
  // Legal Status
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Estado Legal', 14, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Puede Contratar: ${result.legal_status.can_contract ? 'SÍ' : 'NO'}`, 14, yPos);
  yPos += 8;
  
  if (result.legal_status.warnings?.length > 0) {
    doc.setFontSize(10);
    doc.setTextColor(234, 88, 12);
    result.legal_status.warnings.forEach(warning => {
      doc.text(`⚠ ${warning.description}`, 14, yPos, { maxWidth: pageWidth - 28 });
      yPos += 6;
    });
    doc.setTextColor(0, 0, 0);
    yPos += 5;
  }
  
  // Financial Status
  if (result.financial_status) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Situación Financiera', 14, yPos);
    yPos += 8;
    
    const financialData = [
      ['Estado Tributario', result.financial_status.tax_status || 'N/A'],
      ['Score Crediticio', result.financial_status.credit_score?.score?.toString() || 'N/A'],
      ['Clasificación', result.financial_status.credit_score?.classification || 'N/A'],
      ['Deudas Activas', result.financial_status.active_debts?.toString() || '0']
    ];
    
    doc.autoTable({
      startY: yPos,
      body: financialData,
      theme: 'plain',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 45 },
        1: { cellWidth: 120 }
      },
      styles: { fontSize: 10 }
    });
    
    yPos = doc.lastAutoTable.finalY + 10;
    
    // Morosities
    if (result.financial_status.morosities?.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(220, 38, 38);
      doc.text('Morosidades Registradas', 14, yPos);
      yPos += 6;
      doc.setTextColor(0, 0, 0);
      
      const morosityData = result.financial_status.morosities.map(m => [
        m.acreedor,
        `$${m.monto.toLocaleString()}`,
        m.fecha
      ]);
      
      doc.autoTable({
        startY: yPos,
        head: [['Acreedor', 'Monto', 'Fecha']],
        body: morosityData,
        theme: 'striped',
        headStyles: { fillColor: [220, 38, 38] },
        styles: { fontSize: 9 }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }
  }
  
  // Litigation History
  if (result.litigation_history) {
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Historial de Litigios', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Causas Activas: ${result.litigation_history.active_cases.count}`, 14, yPos);
    doc.text(`Causas Históricas: ${result.litigation_history.historical_cases.count}`, 80, yPos);
    yPos += 10;
    
    if (result.litigation_history.active_cases.cases?.length > 0) {
      const casesData = result.litigation_history.active_cases.cases.map(c => [
        c.rol,
        c.tribunal,
        c.materia,
        c.calidad
      ]);
      
      doc.autoTable({
        startY: yPos,
        head: [['Rol', 'Tribunal', 'Materia', 'Calidad']],
        body: casesData,
        theme: 'striped',
        headStyles: { fillColor: [51, 65, 85] },
        styles: { fontSize: 8 }
      });
      
      yPos = doc.lastAutoTable.finalY + 10;
    }
  }
  
  // Assets
  if (result.assets && (result.assets.real_estate.count > 0 || result.assets.vehicles.count > 0)) {
    if (yPos > 230) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Bienes Registrados', 14, yPos);
    yPos += 8;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Propiedades: ${result.assets.real_estate.count}`, 14, yPos);
    doc.text(`Vehículos: ${result.assets.vehicles.count}`, 80, yPos);
    
    if (result.assets.real_estate.total_encumbered > 0) {
      doc.text(`Gravámenes: $${result.assets.real_estate.total_encumbered.toLocaleString()}`, 140, yPos);
    }
    yPos += 10;
  }
  
  // Alerts Section
  if (result.alerts?.length > 0) {
    if (yPos > 230) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Alertas', 14, yPos);
    yPos += 8;
    
    result.alerts.forEach(alert => {
      const alertColor = alert.type === 'CRÍTICO' ? [220, 38, 38] :
                         alert.type === 'ALTO' ? [234, 88, 12] :
                         alert.type === 'MEDIO' ? [202, 138, 4] : [59, 130, 246];
      
      doc.setFillColor(...alertColor);
      doc.roundedRect(14, yPos, pageWidth - 28, 18, 2, 2, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text(`[${alert.type}] ${alert.title}`, 18, yPos + 7);
      doc.setFont('helvetica', 'normal');
      doc.text(alert.message, 18, yPos + 14, { maxWidth: pageWidth - 36 });
      
      yPos += 22;
      doc.setTextColor(0, 0, 0);
    });
  }
  
  // Recommendation
  doc.setFontSize(11);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100, 100, 100);
  
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.text(`Recomendación: ${result.risk_score.recommendation}`, 14, yPos + 10, { maxWidth: pageWidth - 28 });
  
  // Add Verification Seal on last page if verification code provided
  if (verificationCode) {
    const lastPage = doc.internal.getNumberOfPages();
    doc.setPage(lastPage);
    const pageHeight = doc.internal.pageSize.getHeight();
    // Add new page if not enough space
    if (yPos > pageHeight - 80) {
      doc.addPage();
    }
    drawVerificationSeal(doc, 14, doc.internal.pageSize.getHeight() - 65, verificationCode);
  }
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    const footerY = verificationCode && i === pageCount ? doc.internal.pageSize.getHeight() - 70 : doc.internal.pageSize.getHeight() - 10;
    doc.text(
      `Generado por NotaryOS - Página ${i} de ${pageCount}`,
      pageWidth / 2,
      footerY,
      { align: 'center' }
    );
    
    if (result.is_mock_data && (i !== pageCount || !verificationCode)) {
      doc.setTextColor(234, 88, 12);
      doc.text(
        'DATOS DE DEMOSTRACIÓN - En producción se conecta a servicios reales',
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 5,
        { align: 'center' }
      );
    }
  }
  
  return doc;
};

// Download PDF helper
export const downloadPDF = (doc, filename) => {
  doc.save(filename);
};
